// next-auth/react in the React app. The session is the signed-in user from
// `me.get`, which the app root loads before any page renders.
import { createContext, useContext, type ReactNode } from 'react';

export interface ClientSession {
    user: {
        id: string;
        email: string | null;
        image: string | null;
        organizationId: string | null;
    };
    expires: string;
}

const SessionContext = createContext<ClientSession | null>(null);

export function AppSessionProvider({
    session,
    children
}: {
    session: ClientSession | null;
    children: ReactNode;
}) {
    return (
        <SessionContext.Provider value={session}>
            {children}
        </SessionContext.Provider>
    );
}

/** Accepted for compatibility; AppSessionProvider holds the session. */
export function SessionProvider({ children }: { children: ReactNode }) {
    return children;
}

export function useSession(): {
    data: ClientSession | null;
    status: 'authenticated' | 'unauthenticated';
    update: () => Promise<ClientSession | null>;
} {
    const session = useContext(SessionContext);
    return {
        data: session,
        status: session ? 'authenticated' : 'unauthenticated',
        update: async () => session
    };
}

/** Ends the Auth.js session, then loads the sign-in page. */
export async function signOut(
    options: { callbackUrl?: string; redirectTo?: string } = {}
): Promise<void> {
    const csrf = await fetch('/api/auth/csrf', { credentials: 'same-origin' });
    const { csrfToken } = (await csrf.json()) as { csrfToken: string };
    await fetch('/api/auth/signout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ csrfToken })
    });
    window.location.assign(
        options.redirectTo ?? options.callbackUrl ?? '/signin'
    );
}
